/**
 * Render a strict JSON Schema (as produced by OpenAI's `zodResponsesFunction`)
 * as compact, indented plain text for humans to skim. This is lossy by design —
 * it drops strict-mode artefacts (`additionalProperties: false`, `$schema`) and
 * collapses `anyOf: [T, {type:'null'}]` back to `T | null`, which is how the
 * underlying Zod source reads.
 *
 * Not every JSON Schema construct is handled — only what OpenAI strict mode
 * actually emits for us. Unknown shapes fall back to compact JSON so nothing
 * is silently hidden.
 */

type JsonSchema = Record<string, unknown> & {
  $defs?: Record<string, JsonSchema>;
  definitions?: Record<string, JsonSchema>;
};

const INDENT = '  ';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** True when `node.type` is `t` (string form) or contains `t` (array-of-types form). */
const matchesType = (node: Record<string, unknown>, t: string): boolean =>
  node.type === t || (Array.isArray(node.type) && node.type.includes(t));

/** True when `node.type` is exactly `t` (single string or array with only `t`). */
const matchesTypeOnly = (node: Record<string, unknown>, t: string): boolean =>
  node.type === t ||
  (Array.isArray(node.type) && node.type.length === 1 && node.type.includes(t));

const resolveRef = (
  ref: string,
  definitions: Record<string, JsonSchema>,
): JsonSchema | undefined => {
  const match = /^#\/(\$defs|definitions)\/(.+)$/.exec(ref);
  if (!match) return undefined;
  return definitions[match[2]!];
};

const formatConstraints = (node: Record<string, unknown>): string => {
  const bits: string[] = [];
  const min =
    typeof node.minimum === 'number'
      ? `≥ ${node.minimum}`
      : typeof node.exclusiveMinimum === 'number'
        ? `> ${node.exclusiveMinimum}`
        : undefined;
  const max =
    typeof node.maximum === 'number'
      ? `≤ ${node.maximum}`
      : typeof node.exclusiveMaximum === 'number'
        ? `< ${node.exclusiveMaximum}`
        : undefined;
  if (min && max) bits.push(`${min}, ${max}`);
  else if (min) bits.push(min);
  else if (max) bits.push(max);

  if (
    typeof node.minLength === 'number' ||
    typeof node.maxLength === 'number'
  ) {
    const a = node.minLength ?? 0;
    const b = node.maxLength ?? '∞';
    bits.push(`length ${a}..${b}`);
  }
  if (typeof node.minItems === 'number' || typeof node.maxItems === 'number') {
    const a = node.minItems ?? 0;
    const b = node.maxItems ?? '∞';
    bits.push(`${a}..${b} items`);
  }
  if (typeof node.pattern === 'string') bits.push(`pattern /${node.pattern}/`);
  return bits.length ? ` (${bits.join(', ')})` : '';
};

const formatEnum = (values: unknown[]): string =>
  values.map((v) => JSON.stringify(v)).join(' | ');

/** Return `{ inner, isNullable }` for `anyOf: [T, {type:'null'}]`, else undefined. */
const stripNullable = (
  node: Record<string, unknown>,
): { inner: Record<string, unknown>; isNullable: boolean } | undefined => {
  if (!Array.isArray(node.anyOf) && !Array.isArray(node.oneOf))
    return undefined;
  const variants = (node.anyOf ?? node.oneOf) as unknown[];
  if (variants.length !== 2) return undefined;
  const nullIdx = variants.findIndex(
    (v) => isPlainObject(v) && v.type === 'null',
  );
  if (nullIdx === -1) return undefined;
  const other = variants[1 - nullIdx];
  if (!isPlainObject(other)) return undefined;
  return { inner: other, isNullable: true };
};

const typeLabel = (
  node: Record<string, unknown>,
  definitions: Record<string, JsonSchema>,
  indent: string,
): string => {
  if (typeof node.$ref === 'string') {
    const resolved = resolveRef(node.$ref, definitions);
    if (resolved) return typeLabel(resolved, definitions, indent);
    return node.$ref;
  }

  const nullable = stripNullable(node);
  if (nullable) {
    return `${typeLabel(nullable.inner, definitions, indent)} | null`;
  }

  const type = typeof node.type === 'string' ? node.type : undefined;
  const types = Array.isArray(node.type) ? (node.type as string[]) : undefined;

  if (matchesTypeOnly(node, 'array')) {
    const items = isPlainObject(node.items) ? node.items : undefined;
    if (!items) return 'array';
    const itemsNullable = stripNullable(items);
    const inner = itemsNullable ? itemsNullable.inner : items;
    const innerConstraints = formatConstraints(inner);
    const core = `${typeLabel(inner, definitions, indent)}${innerConstraints}${
      itemsNullable ? ' | null' : ''
    }`;
    return `array of ${core}`;
  }

  if (matchesTypeOnly(node, 'object')) {
    return 'object';
  }

  if (types) {
    return types.map((t) => (t === 'null' ? 'null' : t)).join(' | ');
  }

  if (Array.isArray(node.enum)) return formatEnum(node.enum);
  if ('const' in node) return JSON.stringify(node.const);

  if (Array.isArray(node.anyOf) || Array.isArray(node.oneOf)) {
    const variants = (node.anyOf ?? node.oneOf) as unknown[];
    return variants
      .map((v) =>
        isPlainObject(v) ? typeLabel(v, definitions, indent) : String(v),
      )
      .join(' | ');
  }

  return type ?? 'unknown';
};

const renderNode = (
  node: Record<string, unknown>,
  definitions: Record<string, JsonSchema>,
  indent: string,
): string[] => {
  if (typeof node.$ref === 'string') {
    const resolved = resolveRef(node.$ref, definitions);
    if (resolved) return renderNode(resolved, definitions, indent);
    return [];
  }

  const nullable = stripNullable(node);
  if (nullable) {
    return renderNode(nullable.inner, definitions, indent);
  }

  if (matchesType(node, 'array')) {
    let items = isPlainObject(node.items)
      ? (node.items as Record<string, unknown>)
      : undefined;
    if (!items) return [];
    const itemsNullable = stripNullable(items);
    if (itemsNullable) items = itemsNullable.inner;
    if (typeof items.$ref === 'string') {
      const resolved = resolveRef(items.$ref, definitions);
      if (resolved) items = resolved;
    }
    if (matchesType(items, 'object')) {
      return renderObjectFields(items, definitions, indent);
    }
    return [];
  }

  if (matchesType(node, 'object')) {
    return renderObjectFields(node, definitions, indent);
  }

  return [];
};

const renderObjectFields = (
  node: Record<string, unknown>,
  definitions: Record<string, JsonSchema>,
  indent: string,
): string[] => {
  const properties = isPlainObject(node.properties) ? node.properties : {};
  const required = new Set(
    Array.isArray(node.required) ? (node.required as string[]) : [],
  );

  const lines: string[] = [];
  for (const [name, raw] of Object.entries(properties)) {
    if (!isPlainObject(raw)) continue;

    const field: Record<string, unknown> = raw;
    const nullable = stripNullable(field);
    const canonical = nullable ? nullable.inner : field;

    const baseType = typeLabel(field, definitions, indent);
    const reqMarker = required.has(name) ? '' : ' (optional)';
    const constraints = formatConstraints(canonical);
    const description =
      typeof field.description === 'string'
        ? field.description
        : typeof canonical.description === 'string'
          ? canonical.description
          : undefined;

    lines.push(`${indent}**${name}**: ${baseType}${constraints}${reqMarker}`);
    if (description) {
      for (const descLine of description.split('\n')) {
        lines.push(`${indent}${INDENT}— ${descLine}`);
      }
    }
    const nested = renderNode(canonical, definitions, indent + INDENT);
    lines.push(...nested);
  }
  return lines;
};

/** Pretty-print a strict JSON Schema tool parameters block. */
export const humanizeJsonSchema = (schema: unknown): string => {
  if (!isPlainObject(schema)) return String(schema);
  const definitions = (schema.$defs ?? schema.definitions ?? {}) as Record<
    string,
    JsonSchema
  >;
  const top = schema as Record<string, unknown>;

  if (matchesType(top, 'object')) {
    const body = renderObjectFields(top, definitions, '');
    return body.length ? body.join('\n') : '(no parameters)';
  }

  const label = typeLabel(top, definitions, '');
  const nested = renderNode(top, definitions, INDENT);
  return nested.length ? [label, ...nested].join('\n') : label;
};
